import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Sparkles, EyeOff, Eye, Layers, Pencil, Trash2 } from 'lucide-react';
import api from '../lib/axios';
import Spinner from '../components/Spinner';

type FormState = {
  number: string;
  label: string;
  accountClass: string;
  type: string;
  parentNumber: string;
};

const TYPES = ['ASSET', 'LIABILITY', 'EXPENSE', 'INCOME'];

const EMPTY: FormState = { number: '', label: '', accountClass: '', type: 'INCOME', parentNumber: '' };

/** Orders a flat account list into parent → children sequence with a depth for indentation. */
function buildTree(accounts: any[]): { account: any; depth: number }[] {
  const byNumber = new Map<string, any>(accounts.map((a) => [a.number, a]));
  const childrenOf = new Map<string, any[]>();
  const roots: any[] = [];
  for (const a of accounts) {
    const parent = a.parentNumber && byNumber.has(a.parentNumber) ? a.parentNumber : null;
    if (parent) {
      const list = childrenOf.get(parent) ?? [];
      list.push(a);
      childrenOf.set(parent, list);
    } else {
      roots.push(a);
    }
  }
  const out: { account: any; depth: number }[] = [];
  const walk = (a: any, depth: number) => {
    out.push({ account: a, depth });
    for (const child of childrenOf.get(a.number) ?? []) {
      walk(child, depth + 1);
    }
  };
  roots.forEach((r) => walk(r, 0));
  return out;
}

export default function ChartOfAccounts({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingSystem, setEditingSystem] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [aiSource, setAiSource] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateProfile, setTemplateProfile] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get('/accounts').then((r) => r.data),
  });

  const accounts = useMemo(() => data ?? [], [data]);
  const parent = form.parentNumber
    ? accounts.find((a: any) => a.number === form.parentNumber)
    : null;

  const rows = useMemo(() => {
    const visible = showInactive ? accounts : accounts.filter((a: any) => a.active);
    return buildTree(visible);
  }, [accounts, showInactive]);

  const openForm = (parentNumber = '') => {
    const p = parentNumber ? accounts.find((a: any) => a.number === parentNumber) : null;
    setForm({
      ...EMPTY,
      parentNumber,
      accountClass: p?.accountClass ? String(p.accountClass) : '',
      type: p?.type ?? 'INCOME',
    });
    setEditingId(null);
    setEditingSystem(false);
    setAiDesc('');
    setAiSource(null);
    setError(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEdit = (account: any) => {
    setForm({
      number: account.number,
      label: account.label,
      accountClass: account.accountClass != null ? String(account.accountClass) : '',
      type: account.type,
      parentNumber: account.parentNumber ?? '',
    });
    setEditingId(account.id);
    setEditingSystem(!!account.systemAccount);
    setAiDesc('');
    setAiSource(null);
    setError(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const suggest = useMutation({
    mutationFn: () =>
      api
        .post('/accounts/suggest', {
          label: aiDesc,
          parentNumber: form.parentNumber || null,
        })
        .then((r) => r.data),
    onSuccess: (s: any) => {
      setForm((f) => ({
        ...f,
        number: s.number ?? f.number,
        label: s.label ?? f.label,
        accountClass: s.accountClass != null ? String(s.accountClass) : f.accountClass,
        type: s.type ?? f.type,
        parentNumber: s.parentNumber ?? f.parentNumber,
      }));
      setAiSource(s.source ?? null);
    },
  });

  const create = useMutation({
    mutationFn: () =>
      api
        .post('/accounts', {
          number: form.number.trim(),
          label: form.label.trim(),
          accountClass: Number(form.accountClass),
          type: form.type,
          nature: 'DETAIL',
          parentNumber: form.parentNumber || null,
          vatApplicable: false,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      setShowForm(false);
      setForm({ ...EMPTY });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('chartOfAccounts.createError')),
  });

  const update = useMutation({
    mutationFn: () =>
      api
        .put(`/accounts/${editingId}`, {
          number: form.number.trim(),
          label: form.label.trim(),
          accountClass: Number(form.accountClass),
          type: form.type,
          nature: 'DETAIL',
          parentNumber: form.parentNumber || null,
          vatApplicable: false,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      setShowForm(false);
      setEditingId(null);
      setForm({ ...EMPTY });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('chartOfAccounts.createError')),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => api.delete(`/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
    onError: () => window.alert(t('chartOfAccounts.cannotDeactivate')),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/accounts/${id}/permanent`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
    onError: () => window.alert(t('chartOfAccounts.cannotDelete')),
  });

  const reactivate = useMutation({
    mutationFn: (id: number) => api.post(`/accounts/${id}/reactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });

  const { data: templates } = useQuery({
    queryKey: ['account-templates'],
    queryFn: () => api.get('/accounts/templates').then((r) => r.data as any[]),
    enabled: showTemplates,
  });

  const applyTemplate = useMutation({
    mutationFn: (profile: string) =>
      api.post('/accounts/apply-template', null, { params: { profile } }).then((r) => r.data),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      setShowTemplates(false);
      setTemplateProfile('');
      window.alert(
        t('chartOfAccounts.templateApplied', {
          activated: res.activated,
          deactivated: res.deactivated,
        }),
      );
    },
    onError: () => window.alert(t('chartOfAccounts.templateError')),
  });

  const submit = () => {
    setError(null);
    if (!form.number.trim() || !form.label.trim() || !form.accountClass) {
      setError(t('chartOfAccounts.createError'));
      return;
    }
    if (editingId != null) {
      update.mutate();
    } else {
      create.mutate();
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          {!embedded && (
            <>
              <p className="eyebrow">{t('nav.chartOfAccounts')}</p>
              <h1 className="page-title mt-1">{t('chartOfAccounts.title')}</h1>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTemplates(true)}
            className="btn btn-ghost"
            title={t('chartOfAccounts.applyTemplate')}
          >
            <Layers size={16} />
            <span className="hidden sm:inline">{t('chartOfAccounts.applyTemplate')}</span>
          </button>
          <button
            onClick={() => setShowInactive((v) => !v)}
            className="btn btn-ghost"
            title={t('chartOfAccounts.showInactive')}
          >
            {showInactive ? <Eye size={16} /> : <EyeOff size={16} />}
            <span className="hidden sm:inline">{t('chartOfAccounts.showInactive')}</span>
          </button>
          <button
            onClick={() => (showForm ? setShowForm(false) : openForm())}
            className={showForm ? 'btn btn-secondary' : 'btn btn-primary'}
          >
            {showForm ? (
              t('common.close')
            ) : (
              <>
                <Plus size={16} /> {t('chartOfAccounts.addAccount')}
              </>
            )}
          </button>
        </div>
      </div>

      {showTemplates && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={() => setShowTemplates(false)}
            aria-hidden
          />
          <div className="reveal card relative z-10 w-full max-w-lg p-6">
            <h2 className="font-display text-lg font-semibold">
              {t('chartOfAccounts.templateTitle')}
            </h2>
            <p className="mt-1 text-sm text-ink-muted">{t('chartOfAccounts.templateHelp')}</p>

            <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
              {(templates ?? []).map((tpl) => (
                <button
                  key={tpl.key}
                  type="button"
                  onClick={() => setTemplateProfile(tpl.key)}
                  className={`w-full rounded-xl border p-3 text-left transition-colors ${
                    templateProfile === tpl.key
                      ? 'border-brand bg-brand-tint'
                      : 'border-line hover:bg-surface-raised'
                  }`}
                >
                  <div className="font-medium text-ink">{tpl.label}</div>
                  <div className="mt-0.5 text-xs text-ink-muted">{tpl.description}</div>
                </button>
              ))}
            </div>

            <p className="mt-4 text-xs text-ink-muted">{t('chartOfAccounts.templateWarning')}</p>

            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowTemplates(false)}
                className="btn btn-secondary"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => templateProfile && applyTemplate.mutate(templateProfile)}
                disabled={!templateProfile || applyTemplate.isPending}
                className="btn btn-primary"
              >
                {applyTemplate.isPending ? <Spinner /> : <Layers size={16} />}
                {t('chartOfAccounts.templateApply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="reveal card mb-6 p-6">
          <h2 className="mb-1 font-display text-lg font-semibold">
            {editingId != null
              ? t('chartOfAccounts.editAccount')
              : parent
                ? t('chartOfAccounts.newSubAccountOf', { number: parent.number, label: parent.label })
                : t('chartOfAccounts.newAccount')}
          </h2>

          {editingId == null && (
            <div className="surface-muted mt-4 rounded-xl p-4">
            <label className="field-label">{t('chartOfAccounts.aiDescribe')}</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                value={aiDesc}
                onChange={(e) => setAiDesc(e.target.value)}
                className="input flex-1"
                placeholder={t('chartOfAccounts.aiPlaceholder')}
              />
              <button
                type="button"
                onClick={() => aiDesc.trim() && suggest.mutate()}
                disabled={!aiDesc.trim() || suggest.isPending}
                className="btn btn-secondary shrink-0"
              >
                {suggest.isPending ? <Spinner /> : <Sparkles size={16} />}
                {suggest.isPending ? t('chartOfAccounts.aiSuggesting') : t('chartOfAccounts.aiSuggest')}
              </button>
            </div>
            <p className="mt-2 text-xs text-ink-muted">{t('chartOfAccounts.aiHint')}</p>
            {aiSource && (
              <p className="mt-1 text-xs font-medium text-brand">
                {t('chartOfAccounts.aiFrom', {
                  source:
                    aiSource === 'ai'
                      ? t('chartOfAccounts.aiSourceAi')
                      : t('chartOfAccounts.aiSourceHeuristic'),
                })}
              </p>
            )}
          </div>
          )}

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="field-label">{t('field.number')}</label>
              <input
                value={form.number}
                onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
                className="input tnum"
                placeholder="756"
                disabled={editingId != null}
              />
            </div>
            <div>
              <label className="field-label">{t('field.label')}</label>
              <input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="field-label">{t('field.class')}</label>
              <input
                type="number"
                min="1"
                max="8"
                value={form.accountClass}
                onChange={(e) => setForm((f) => ({ ...f, accountClass: e.target.value }))}
                className="input tnum"
                disabled={editingSystem}
              />
            </div>
            <div>
              <label className="field-label">{t('field.type')}</label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="select"
                disabled={editingSystem}
              >
                {TYPES.map((ty) => (
                  <option key={ty} value={ty}>
                    {t(`accountType.${ty}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="field-label">{t('chartOfAccounts.parent')}</label>
              <select
                value={form.parentNumber}
                onChange={(e) => setForm((f) => ({ ...f, parentNumber: e.target.value }))}
                className="select"
              >
                <option value="">{t('chartOfAccounts.noParent')}</option>
                {accounts.map((a: any) => (
                  <option key={a.number} value={a.number}>
                    {a.number} - {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1" />
            {error && <span className="text-sm font-medium text-expense">{error}</span>}
            <button
              onClick={submit}
              disabled={create.isPending || update.isPending}
              className="btn btn-primary"
            >
              {editingId != null
                ? update.isPending
                  ? t('chartOfAccounts.saving')
                  : t('chartOfAccounts.save')
                : create.isPending
                  ? t('chartOfAccounts.creating')
                  : t('chartOfAccounts.create')}
            </button>
          </div>
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{t('field.number')}</th>
              <th>{t('field.label')}</th>
              <th>{t('field.type')}</th>
              <th>{t('field.active')}</th>
              <th className="text-right">{t('field.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="py-8 text-center text-ink-muted">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {rows.map(({ account: c, depth }) => (
              <tr key={c.id ?? c.number} className={c.active ? '' : 'opacity-55'}>
                <td data-label={t('field.number')} className="font-medium tnum" style={{ paddingLeft: `${depth * 1.5 + 0.75}rem` }}>
                  {c.number}
                </td>
                <td data-label={t('field.label')}>{c.label}</td>
                <td data-label={t('field.type')}>
                  <span className="text-ink-soft">{t(`accountType.${c.type}`)}</span>
                </td>
                <td data-label={t('field.active')}>
                  <span className={`pill ${c.active ? 'pill-posted' : 'pill-neutral'}`}>
                    {c.active ? t('chartOfAccounts.active') : t('chartOfAccounts.inactive')}
                  </span>
                </td>
                <td className="cell-actions">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => openForm(c.number)}
                      className="btn btn-ghost !px-2 !py-1 text-xs"
                      title={t('chartOfAccounts.addSubAccount')}
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      onClick={() => openEdit(c)}
                      className="btn btn-ghost !px-2 !py-1 text-xs"
                      title={t('chartOfAccounts.edit')}
                    >
                      <Pencil size={14} />
                    </button>
                    {!c.systemAccount && (
                      <button
                        onClick={() =>
                          window.confirm(t('chartOfAccounts.confirmDelete')) && remove.mutate(c.id)
                        }
                        className="btn btn-ghost !px-2 !py-1 text-xs text-expense"
                        title={t('chartOfAccounts.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    {c.active ? (
                      <button
                        onClick={() =>
                          window.confirm(t('chartOfAccounts.confirmDeactivate')) &&
                          deactivate.mutate(c.id)
                        }
                        className="btn btn-ghost !px-2 !py-1 text-xs"
                      >
                        {t('chartOfAccounts.deactivate')}
                      </button>
                    ) : (
                      <button
                        onClick={() => reactivate.mutate(c.id)}
                        className="btn btn-ghost !px-2 !py-1 text-xs text-brand"
                      >
                        {t('chartOfAccounts.reactivate')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
